import { firstOccurrence, nextOccurrence, parse } from '@focuslee/shared';
import { ButtonDock, miniButtonDockScrollBottomPad, WheelColumn } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Text } from '@telegram-apps/telegram-ui';
import dayjs from 'dayjs';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RepeatCreateInput } from '../../client/generated/graphql';
import { dayjsStartOfDay, toDueString, toDueStringSimple } from '../../helpers/datetime';
import { getRepeatFromStr, getRepeatText } from '../../helpers/quest';
import { getButtonThemeColors } from '../../helpers/theme';
import { Page } from '../Page';
import { CommonIcon } from '../common/CommonIcon';
import { CustomInput } from '../common/CustomInput';
import { LiquidGlassChip } from '../common/LiquidGlassChip';
import { QuestCalendarFullView } from './QuestCalendarFullView';

interface QuestDateSettingsProps {
  initialDate: Date | null;
  initialRepeat: RepeatCreateInput | null;
  isLoading?: boolean;
  onSave: (settings: { date: Date | null, repeat: RepeatCreateInput | null }) => Promise<void>;
  onCancel: () => void;
}

const pad2 = (i: number) => String(i).padStart(2, '0');

export const QuestDateSettings: FC<QuestDateSettingsProps> = ({
  initialDate,
  initialRepeat,
  isLoading = false,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation('common');
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);
  const [repeat, setRepeat] = useState<RepeatCreateInput | null>(initialRepeat);
  const [dateQuery, setDateQuery] = useState<string>(initialRepeat?.query || '');
  const [isQueryInputFocused, setIsQueryInputFocused] = useState<boolean>(false);

  const handleQuickSelect = useCallback(async (date?: Date | null) => {
    const newDate = date === undefined ? null : date;
    setSelectedDate(newDate);
    await onSave({ date: newDate, repeat });
  }, [onSave, repeat]);

  const handleDateQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setDateQuery(query);

    const parseResult = getRepeatFromStr(query);
    if (parseResult) {
      const [parsedRepeat, parsingDetails] = parseResult;
      const firstDate = firstOccurrence(parsingDetails);
      setRepeat(parsedRepeat);
      setSelectedDate(firstDate ? firstDate.toDate() : null);
    } else {
      setRepeat(null);
    }
  };

  const selectedDateDayjs = selectedDate ? dayjs(selectedDate) : null;
  const currentHour = selectedDateDayjs ? selectedDateDayjs.hour() : 0;
  const currentMinute = selectedDateDayjs ? selectedDateDayjs.minute() : 0;

  const today = dayjsStartOfDay(dayjs()).add(currentHour, 'hour').add(currentMinute, 'minute');
  const tomorrow = today.add(1, 'day');
  const nextMonday = today.weekday() >= 1
    ? today.add(8 - today.weekday(), 'day')
    : today.add(1, 'day');
  const postponeDate = repeat ? nextOccurrence(parse(repeat.query)!, today) : null;

  const handleCalendarDaySelect = useCallback((day: dayjs.Dayjs) => {
    setSelectedDate(day.hour(currentHour).minute(currentMinute).second(0).millisecond(0).toDate());
  }, [currentHour, currentMinute]);

  const handleTimeChange = useCallback((hour: number, minute: number) => {
    const base = selectedDateDayjs ?? dayjsStartOfDay(dayjs());
    setSelectedDate(base.hour(hour).minute(minute).second(0).millisecond(0).toDate());
  }, [selectedDateDayjs]);

  const handleSave = useCallback(async () => {
    await onSave({
      date: selectedDate,
      repeat: repeat ? {...repeat, query: getRepeatText(repeat)} : null
    });
  }, [selectedDate, repeat]);

  const repeatQuerySpecified = repeat && repeat.query && repeat.query.trim() !== '';

  return (
    <Page
      back={true}
      insetEnabled={false}
      edgeToEdge
      backClick={onCancel}
      footer={
        <ButtonDock
          secondary={{ text: t('actions.cancel'), onClick: onCancel, navigation: 'back' }}
          main={{
            text: t('actions.save'),
            onClick: handleSave,
            loading: isLoading,
            bgColor: getButtonThemeColors(true).backgroundColor,
          }}
        />
      }
    >
      <Box sx={{ height: '100%', position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            paddingTop: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
          }}
        >
        <Stack
          paddingLeft="10px"
          paddingRight="10px"
          paddingTop="10px"
          paddingBottom={miniButtonDockScrollBottomPad}
          gap={1.5}
        >
          <CustomInput
            autoCapitalize='none'
            placeholder={t('quest.repeatQuery')}
            value={dateQuery}
            onChange={handleDateQueryChange}
            wrapperStyle={{ minHeight: 0 }}
            onFocus={() => setIsQueryInputFocused(true)}
            onBlur={() => setIsQueryInputFocused(false)}
          />

          {(selectedDate || repeatQuerySpecified) && (
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              sx={{
                paddingLeft: '2px',
                marginTop: '4px',
                display: isQueryInputFocused && !repeatQuerySpecified ? 'none' : 'flex',
                cursor: isQueryInputFocused ? 'pointer' : 'default',
                backgroundColor: isQueryInputFocused ? 'var(--tg-theme-section-bg-color)' : 'transparent',
                borderRadius: isQueryInputFocused ? '4px' : '0px',
                padding: isQueryInputFocused ? '8px' : '0px'
              }}
            >
              {selectedDate && (
                <>
                  <CommonIcon icon="event" type="filled" size={16} color="var(--tg-theme-hint-color)"/>
                  <Text type="footnote" style={{ color: 'var(--tg-theme-hint-color)' }}>
                    {toDueString(dayjs(selectedDate))}
                  </Text>
                </>
              )}
              {selectedDate && repeatQuerySpecified && (
                <Text type="footnote" style={{ color: 'var(--tg-theme-hint-color)', marginLeft: '4px', marginRight: '4px' }}>
                  •
                </Text>
              )}
              {repeatQuerySpecified && (
                <>
                  <CommonIcon icon="repeat" type="filled" size={16} color="var(--tg-theme-hint-color)"/>
                  <Text type="footnote" style={{ color: 'var(--tg-theme-hint-color)' }}>
                    {getRepeatText(repeat)}
                  </Text>
                </>
              )}
            </Stack>
          )}

          {!isQueryInputFocused && (
            <>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {selectedDateDayjs?.isSame(today, 'day') ? (
                  <LiquidGlassChip
                    icon={<CommonIcon icon="wb_sunny" type="filled" size={20} color="var(--tg-theme-text-color)"/>}
                    label={t('quest.tomorrow')}
                    subLabel={toDueStringSimple(tomorrow)}
                    onClick={() => handleQuickSelect(tomorrow.toDate())}
                  />
                ) : (
                  <LiquidGlassChip
                    icon={<CommonIcon icon="calendar-today" type="filled" size={20} color="var(--tg-theme-text-color)"/>}
                    label={t('quest.today')}
                    subLabel={toDueStringSimple(today)}
                    onClick={() => handleQuickSelect(today.toDate())}
                  />
                )}
                <LiquidGlassChip
                  icon={<CommonIcon icon="date_range" type="filled" size={20} color="var(--tg-theme-text-color)"/>}
                  label={t('quest.nextWeek')}
                  subLabel={toDueStringSimple(nextMonday)}
                  onClick={() => handleQuickSelect(nextMonday.toDate())}
                />
                {postponeDate && (
                  <LiquidGlassChip
                    icon={<CommonIcon icon="schedule" type="filled" size={20} color="var(--tg-theme-text-color)"/>}
                    label={t('quest.postpone')}
                    subLabel={toDueStringSimple(postponeDate)}
                    onClick={() => handleQuickSelect(postponeDate.toDate())}
                  />
                )}
                {selectedDate && (
                  <LiquidGlassChip
                    icon={<CommonIcon icon="event_busy" type="filled" size={20} color="var(--tg-theme-text-color)"/>}
                    label={t('quest.noDate')}
                    onClick={() => handleQuickSelect(null)}
                  />
                )}
              </Stack>

              <Stack direction="row" alignItems="center" gap="10px">
                <CommonIcon icon="schedule" type="filled" size={18} color="var(--tg-theme-hint-color)" />
                <Text style={{ fontSize: 13, color: 'var(--tg-theme-hint-color)', flexShrink: 0 }}>{t('quest.time')}</Text>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="4px" sx={{ flex: 1 }}>
                  <WheelColumn
                    count={24}
                    selectedIndex={currentHour}
                    labelAt={pad2}
                    onCommittedIndex={h => handleTimeChange(h, currentMinute)}
                    selectionOutline="var(--tgui--button_color)"
                    size="md"
                    visibleRows={3}
                  />
                  <Text style={{ fontSize: 22, fontWeight: 800, color: 'var(--tg-theme-hint-color)', lineHeight: 1 }}>:</Text>
                  <WheelColumn
                    count={60}
                    selectedIndex={currentMinute}
                    labelAt={pad2}
                    onCommittedIndex={m => handleTimeChange(currentHour, m)}
                    selectionOutline="var(--tgui--button_color)"
                    size="md"
                    visibleRows={3}
                  />
                </Stack>
              </Stack>

              <QuestCalendarFullView
                selectedDate={selectedDateDayjs ?? today}
                onDateSelected={handleCalendarDaySelect}
                variant="inline"
              />
            </>
          )}
        </Stack>
        </Box>
      </Box>
    </Page>
  );
};
